import React, { Component } from 'react'
import { connect } from 'react-redux'
import { HorizontalBar } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import WidgetContainer from 'components/Apps/WidgetContainer'
import {
  fetchSiteSFRData,
  fetchCageSFRData,
  resetCageSFRData,
  setActiveCage,
} from 'reducers/slaMonitor/reducer'
import {
  DEFAULT_CHART_COLOR_RGBA,
  DEFAULT_CHART_COLOR_RGBA_HOVER,
} from 'constants/insight'
import messages from 'components/Apps/SlaMonitor/translate'
import { mathRound, toFixed } from 'helpers/math'
import { getChartsRatio, getActiveLocus } from 'helpers/slaMonitor'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import CircleLoader from 'components/Loading/CircleLoader'

class SiteSFRCorrected extends Component {
  constructor(props) {
    super(props)
    this.state = {
      barIndex: 0,
    }
    this.dataStructure = {
      index: null,
      id: 0,
      locus: {
        locus_id: 0,
        locus_name: 'N/A',
        sfr_observed: 0,
        sfr_expected: 0,
        sfr_corrected: 0,
      },
    }
  }

  componentDidMount() {
    const { activeSite, activeDate } = this.props
    this.fetchSiteSFRData(activeSite.id, activeDate)
  }

  shouldComponentUpdate(nextProps) {
    const {
      activeSite,
      activeDate,
      activeCage,
      siteSFRData,
      resetCageSFRData,
    } = this.props

    if (
      activeSite !== nextProps.activeSite ||
      activeDate !== nextProps.activeDate
    ) {
      this.fetchSiteSFRData(nextProps.activeSite.id, nextProps.activeDate)

      return false
    }

    if (
      activeCage !== nextProps.activeCage ||
      siteSFRData !== nextProps.siteSFRData
    ) {
      const cage = getActiveLocus(
        this.dataStructure,
        nextProps.activeCage,
        nextProps.siteSFRData,
      )

      this.setState(
        {
          barIndex: +cage.index,
        },
        () => {
          if (cage.id) {
            this.fetchCageSFRData(cage.id, activeDate, cage.locus)
          } else {
            resetCageSFRData()
          }
        },
      )

      return false
    }

    return true
  }

  getChartParams = () => {
    const cages = this.props.siteSFRData.get('locus')
    const { barIndex } = this.state
    const labels = []
    const data = []
    const backgroundColors = []
    const xTicks = {
      beginAtZero: true,
      max: 0,
    }

    for (const key in cages) {
      if (Object.prototype.hasOwnProperty.call(cages, key)) {
        const sfr_corrected = Math.round(cages[key].sfr_corrected * 100)
        labels.push(cages[key].locus_name)
        data.push(sfr_corrected)
        const checkWindowWidth = ifWindowWidthLessThan(992)
          ? DEFAULT_CHART_COLOR_RGBA_HOVER
          : DEFAULT_CHART_COLOR_RGBA

        backgroundColors.push(
          barIndex !== +key ? DEFAULT_CHART_COLOR_RGBA_HOVER : checkWindowWidth,
        )

        if (sfr_corrected > xTicks.max) {
          xTicks.max = sfr_corrected
        }
      }
    }

    xTicks.max = Math.ceil((xTicks.max + xTicks.max * 0.3) / 10) * 10

    return {
      xTicks,
      labels,
      datasets: [
        {
          backgroundColor: backgroundColors,
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data,
        },
      ],
    }
  }

  chartData = ({ labels, datasets }) => ({ labels, datasets })

  chartOptions = ({ xTicks }) => {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.sfrCorrected)} (%)`,
            },
            ticks: xTicks,
          },
        ],
        yAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.cages),
            },
            ticks: {},
          },
        ],
      },
      tooltips: {
        enabled: false,
      },
      plugins: {
        datalabels: {
          anchor: 'end',
          align: 'end',
          color: 'black',
          labels: {
            value: {
              font: {
                weight: 'bold',
                size: ifWindowWidthLessThan(1280) ? 9 : 11,
              },
            },
          },
          listeners: {
            click: this.onBarLabelClick,
          },
          formatter: v => `${v}%`,
        },
      },
    }
  }

  onBarLabelClick = chartEvent => {
    this.setHorizontalBarClick(chartEvent.dataIndex)
  }

  onHorizontalBarClick = chartEvent => {
    if (chartEvent.length) {
      this.setHorizontalBarClick(chartEvent[0]._index)
    }
  }

  setHorizontalBarClick = barIndex => {
    const cage = this.props.siteSFRData.get('locus')[barIndex]

    if (cage) {
      this.props.setActiveCage(cage.locus_id)

      if (ifWindowWidthLessThan(992)) {
        this.props.goToCage()
      }
    }
  }

  fetchSiteSFRData = (siteId, activeDate) => {
    this.props.fetchSiteSFRData({
      siteId,
      date: activeDate.format('YYYY-MM-DD'),
    })
  }

  fetchCageSFRData = (cageId, activeDate, cage) => {
    this.props.fetchCageSFRData({
      locusId: cageId,
      date: activeDate.format('YYYY-MM-DD'),
      locus: cage,
    })
  }

  render() {
    const {
      hideRender,
      siteSFRData,
      chartLoader,
      activeSite,
      intl: { formatMessage },
    } = this.props

    if (hideRender === false) {
      return null
    }

    const chartParams = this.getChartParams()

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.sfrCorrected),
          end: activeSite.name,
        }}
        sideTitle={siteSFRData.get('last_updated')}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-info">
            <div className="info-item">
              <span className="item-count">
                {toFixed(mathRound(siteSFRData.get('sfr_observed')))}
              </span>
              <span className="item-name">
                {formatMessage(messages.sfrObserved)}
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {toFixed(mathRound(siteSFRData.get('sfr_expected')))}
              </span>
              <span className="item-name">
                {formatMessage(messages.sfrExpected)}
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {Math.round(siteSFRData.get('sfr_corrected') * 100)}%
              </span>
              <span className="item-name">
                {formatMessage(messages.sfrCorrected)}
              </span>
            </div>
          </div>
          <div className="chart-content">
            <CircleLoader loading={chartLoader} />
            <HorizontalBar
              width={null}
              height={null}
              data={this.chartData(chartParams)}
              options={this.chartOptions(chartParams)}
              getElementAtEvent={this.onHorizontalBarClick}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    siteSFRData: state.getIn(['slaMonitor', 'siteSFRData']),
    activeSite: state.getIn(['profile', 'profile', 'site']),
    activeDate: state.getIn(['slaMonitor', 'activeDate']),
    activeCage: state.getIn(['slaMonitor', 'activeCage']),
    chartLoader: state.getIn(['slaMonitor', 'siteSFRLoader']),
  }),
  {
    fetchSiteSFRData,
    fetchCageSFRData,
    resetCageSFRData,
    setActiveCage,
  },
)(injectIntl(SiteSFRCorrected))
